<template>
  <v-dialog
    v-model="show"
    :width="dialogWidth"
    :fullscreen="isMobile"
    transition="slide-y-transition"
  >
    <div
      v-if="hasFormDraft"
      class="booking-form d-flex align-stretch"
    >
      <div class="booking-form-inner-container p-relative">
        <v-card
          class="d-flex flex-column height-100 overflow-x-hidden overflow-y-auto"
          :class="{ 'map-displayed': isShowMap }"
          :width="formCardWidth"
          tile
        >
          <booking-form-header-section></booking-form-header-section>

          <v-card-text class="flex-grow-1 pb-0">
            <v-container>
              <booking-form-fields-section
                :show-map-button="showMapButton"
                :is-show-map="isShowMap"
                @category-updated="afterCategoryUpdated"
                @toggle-map="toggleMap"
                @set-position-and-zoom="setPositionAndZoom"
                @set-position-validation="setPositionValidation"
              ></booking-form-fields-section>

              <booking-form-group-section
                v-if="showBookingGroupSection"
                :is-show-map="isShowMap"
                :show-map-button="showMapButton"
                @toggle-map="toggleMap"
                @set-position-validation="setPositionValidation"
              ></booking-form-group-section>
            </v-container>
          </v-card-text>

          <booking-form-actions-section
            :has-position-error="hasPositionError"
          ></booking-form-actions-section>
        </v-card>
      </div>
      <booking-form-map-panel
        v-if="showMapPanel"
        ref="mapPanel"
        :width="mapCardWidth"
        :height="bookingMapHeight"
        @close-map="closeMap"
        @select-position="setPosition"
        @unselect-position="clearPosition"
      ></booking-form-map-panel>
    </div>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import BookingFormActionsSection from '@/components/booking/form/ActionsSection.vue'
import BookingFormFieldsSection from '@/components/booking/form/FieldsSection.vue'
import BookingFormGroupSection from '@/components/booking/form/GroupSection.vue'
import BookingFormHeaderSection from '@/components/booking/form/HeaderSection.vue'
import BookingFormMapPanel from '@/components/booking/form/MapPanel.vue'

const BOOKING_FORM_WIDTH = 800
const BOOKING_FORM_DEFAULT_DIALOG_WIDTH = 900
const BOOKING_MAP_HEIGHT = 790
const BOOKING_MAP_MIN_WIDTH = 564
const BOOKING_MAP_DIALOG_GAP = 48

export default {
  name: 'BookingForm',
  components: {
    BookingFormActionsSection,
    BookingFormFieldsSection,
    BookingFormGroupSection,
    BookingFormHeaderSection,
    BookingFormMapPanel
  },
  data: () => ({
    bookingMapHeight: BOOKING_MAP_HEIGHT,
    positionValidation: null,
    userActivatedMap: false
  }),
  computed: {
    ...mapState({
      pendingCardNumber: state => state.scr.pendingCardNumber,
      bookingType: state => state.bookingForm.bookingType,
      bookingGroup: state => state.bookingForm.bookingGroupDraft,
      isFormDialogOpen: state => state.bookingForm.show
    }),
    ...mapGetters('bookingForm', {
      booking: 'activeBooking',
      isBookingGroup: 'isBookingGroup',
      mapImageAspectRatio: 'mapImageAspectRatio',
      settingAlwaysShowMap: 'settingAlwaysShowMap'
    }),
    show: {
      get: function () {
        return this.isFormDialogOpen
      },
      set: function (value) {
        return this.setShowFormDialog(value)
      }
    },
    isShowMap: {
      get: function () {
        return !this.isMobile && (this.userActivatedMap || this.settingAlwaysShowMap)
      },
      set: function (value) {
        this.userActivatedMap = value
      }
    },
    mapCardWidth: function () {
      const maxCardWidth = window.innerWidth - BOOKING_FORM_WIDTH - BOOKING_MAP_DIALOG_GAP
      const optimalWidth = BOOKING_MAP_HEIGHT / this.mapImageAspectRatio
      return Math.min(Math.max(BOOKING_MAP_MIN_WIDTH, optimalWidth), maxCardWidth)
    },
    showMapButton: function () {
      return !this.isMobile && !this.settingAlwaysShowMap
    },
    dialogWidth: function () {
      if (this.isMobile) {
        return ''
      }

      return this.isShowMap ? `${this.mapCardWidth + BOOKING_FORM_WIDTH}px` : `${BOOKING_FORM_DEFAULT_DIALOG_WIDTH}px`
    },
    formCardWidth: function () {
      if (this.isMobile) {
        return ''
      }

      return this.isShowMap ? `${BOOKING_FORM_WIDTH}px` : `${BOOKING_FORM_DEFAULT_DIALOG_WIDTH}px`
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    hasFormDraft: function () {
      return !!this.booking || !!this.bookingGroup
    },
    showBookingGroupSection: function () {
      return this.isBookingGroup && !!this.bookingGroup
    },
    showMapPanel: function () {
      return !!this.booking && this.isShowMap
    },
    hasPositionError: function () {
      return !!this.positionValidation?.hasError
    }
  },
  methods: {
    ...mapActions('bookingForm', {
      afterFormClosed: 'afterFormClosed',
      afterFormOpened: 'afterFormOpened',
      normalizeActiveBookingGroupBookingIndex: 'normalizeActiveBookingGroupBookingIndex',
      setPendingCardNumber: 'setPendingCardNumber',
      setShowFormDialog: 'setShowFormDialog',
      updatePositionDraft: 'setPosition'
    }),
    closeMap: function () {
      this.isShowMap = false
    },
    resetLocalFormState: function () {
      this.positionValidation = null
      this.userActivatedMap = false
    },
    resetPositionValidation: function () {
      this.positionValidation = null
    },
    afterCategoryUpdated: function ({ category, result }) {
      if (!result) {
        return false
      }

      this.zoomToSelectedCategory(category, result)
      return result
    },
    setPosition: function ({ position, bookingClientId, setCategory = true }) {
      return this.updatePositionDraft({ position, bookingClientId, setCategory })
    },
    clearPosition: function () {
      return this.setPosition({ position: null })
    },
    setPositionAndZoom: function ({ position, bookingClientId }) {
      return this.setPosition({ position, bookingClientId, setCategory: false })
        .then((result) => {
          if (!result) {
            return false
          }

          return this.zoomToSelectedPosition()
        })
    },
    setPositionValidation: function (validation) {
      this.positionValidation = validation
    },
    showMap: function () {
      this.isShowMap = true
      this.$nextTick(function () {
        this.$refs.mapPanel?.zoomToBooking()
      })
    },
    toggleMap: function () {
      this.isShowMap ? this.closeMap() : this.showMap()
    },
    zoomToSelectedCategory: function (category, result) {
      if (this.isShowMap && category && !result.selectedPosition?.id && this.$refs.mapPanel) {
        this.$refs.mapPanel.zoomToCategory(category.id)
        return true
      }

      return false
    },
    zoomToSelectedPosition: function () {
      if (!this.isShowMap) {
        return false
      }

      return this.$refs.mapPanel?.zoomToSelectedPosition() ?? false
    }
  },
  watch: {
    booking: function () {
      this.resetPositionValidation()
      this.normalizeActiveBookingGroupBookingIndex()
    },
    bookingType: function () {
      this.resetPositionValidation()
    },
    isMobile: function () {
      if (this.isMobile) {
        this.isShowMap = false
      }
    },
    pendingCardNumber: function (val) {
      this.setPendingCardNumber(val)
    },
    show: function () {
      if (!this.show) {
        this.resetLocalFormState()
        this.afterFormClosed()
      } else {
        this.resetPositionValidation()
        this.afterFormOpened()
      }
    }
  }
}
</script>
